import React, { useMemo } from "react";
import { hubScanUrl } from "../ControlTower/hubScanLinks";
import { agentGroupKey, variantTitle } from "../issueAnalyticsHelpers";
import { groupMix, REASON_BY_ID, REASON_GROUPS, reasonMix } from "./discardReasons";
import { topPhrases } from "./phrases";

const ROW_CAP = 500;

function ReasonChip({ category, active, onClick, count }) {
  const c = REASON_BY_ID[category] || REASON_BY_ID.other;
  return (
    <button
      type='button'
      onClick={onClick}
      title={`${REASON_GROUPS[c.group].label}: ${REASON_GROUPS[c.group].hint}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-caption border transition-colors ${
        active ? "border-accent-purple bg-accent-purple/10 text-text-primary" : "border-border-subtle bg-white text-text-secondary hover:bg-surface-neutral"
      }`}>
      <span className='inline-block w-2 h-2 rounded-full' style={{ backgroundColor: c.color }} />
      {c.label}
      {count != null && <span className='text-text-tertiary'>{count.toLocaleString()}</span>}
    </button>
  );
}

// Why does this slice get rejected? Reason mix (taxonomy + signal groups), the raw
// phrase counts, and the discarded issues themselves with Hub links.
export default function DrillDown({
  selectionLabel,
  onClearSelection,
  mixDiscards, // discards in the current agent / client slice (before the reason filter)
  discards, // after the reason filter and text search
  reasonFilter,
  onReasonFilter,
  search,
  onSearch,
  onExport,
  websiteById,
  labelForAgent,
  grouping,
  capped,
  hubEnv = "prod",
}) {
  const mix = useMemo(() => reasonMix(mixDiscards), [mixDiscards]);
  const groups = useMemo(() => groupMix(mix), [mix]);
  const phrases = useMemo(() => topPhrases(discards), [discards]);
  const maxPhrase = (phrases[0] && phrases[0][1]) || 1;
  const websiteName = (wid) => (websiteById[wid] && websiteById[wid].website_name) || wid;

  return (
    <div className='bg-white border border-border-subtle rounded-2xl p-4 space-y-4' data-testid='drill-down'>
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div>
          <h2 className='text-h3 text-text-primary'>Drill-down</h2>
          <p className='text-body-xs text-text-secondary'>
            {selectionLabel || "Showing all discarded issues in range. Select an agent, a scan title or a client above to narrow."}
          </p>
        </div>
        {selectionLabel && (
          <button
            type='button'
            onClick={onClearSelection}
            className='px-3 py-1 text-body-xs bg-surface-neutral text-text-secondary rounded-lg hover:bg-surface-neutral-subtle'>
            Clear selection
          </button>
        )}
      </div>

      {/* Reason mix */}
      <div>
        <h3 className='text-body-sm font-semibold text-text-primary mb-1'>
          Why they were discarded ({(mixDiscards || []).length.toLocaleString()} record{(mixDiscards || []).length === 1 ? "" : "s"})
        </h3>
        {mix.length === 0 ? (
          <p className='text-text-tertiary text-body-xs'>No discards in this slice.</p>
        ) : (
          <>
            <div className='flex h-3 rounded-full overflow-hidden bg-surface-neutral' title='Share of discards by reason category'>
              {mix.map((c) => (
                <div key={c.id} style={{ width: `${c.share * 100}%`, backgroundColor: c.color }} title={`${c.label}: ${c.count} (${(c.share * 100).toFixed(0)}%)`} />
              ))}
            </div>
            <div className='flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-caption text-text-secondary'>
              {groups.map((g) => (
                <span key={g.id} title={g.hint}>
                  <span className='font-semibold text-text-primary'>{g.label}</span> {(g.share * 100).toFixed(0)}%
                  <span className='text-text-tertiary'> · {g.hint}</span>
                </span>
              ))}
            </div>
            <div className='flex flex-wrap gap-1.5 mt-2'>
              {mix.map((c) => (
                <ReasonChip key={c.id} category={c.id} count={c.count} active={reasonFilter === c.id} onClick={() => onReasonFilter(reasonFilter === c.id ? "" : c.id)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Phrases */}
      <details className='group'>
        <summary className='cursor-pointer text-body-sm font-semibold text-text-primary'>
          Top phrases in reasons <span className='text-caption font-normal text-text-tertiary'>(words and pairs, current filter)</span>
        </summary>
        {phrases.length === 0 ? (
          <p className='text-text-tertiary text-body-xs mt-2'>No discard reasons in this slice.</p>
        ) : (
          <div className='space-y-1 mt-2'>
            {phrases.map(([word, count]) => (
              <div key={word} className='flex items-center gap-2'>
                <div className='w-40 text-body-xs text-text-secondary truncate'>{word}</div>
                <div className='flex-1 bg-surface-neutral rounded-md h-4 overflow-hidden'>
                  <div className='h-full bg-accent-purple' style={{ width: `${(count / maxPhrase) * 100}%` }} />
                </div>
                <div className='w-10 text-body-xs text-text-secondary text-right'>{count}</div>
              </div>
            ))}
          </div>
        )}
      </details>

      {/* Records */}
      <div>
        <div className='flex items-center justify-between mb-2 gap-2 flex-wrap'>
          <h3 className='text-body-sm font-semibold text-text-primary'>
            Discarded issues ({discards.length.toLocaleString()}){capped ? " · Platform capped the list at 5000" : ""}
          </h3>
          <div className='flex items-center gap-2'>
            {reasonFilter && <ReasonChip category={reasonFilter} active onClick={() => onReasonFilter("")} />}
            <input
              type='text'
              placeholder='Search reason / title…'
              aria-label='Search discarded issues'
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              className='px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[220px]'
            />
            <button
              type='button'
              onClick={onExport}
              className='px-3 py-2 text-body-xs font-semibold bg-accent-purple/10 text-accent-purple rounded-xl hover:bg-accent-purple/20'>
              Export CSV
            </button>
          </div>
        </div>
        <div className='overflow-auto max-h-[500px] border border-border-subtle rounded-xl'>
          <table className='min-w-full text-body-xs'>
            <thead className='sticky top-0 bg-white z-10'>
              <tr className='text-left text-text-secondary'>
                <th className='p-2 border-b border-border-subtle whitespace-nowrap'>When</th>
                <th className='p-2 border-b border-border-subtle'>Agent</th>
                <th className='p-2 border-b border-border-subtle'>Website</th>
                <th className='p-2 border-b border-border-subtle'>Severity</th>
                <th className='p-2 border-b border-border-subtle'>Title</th>
                <th className='p-2 border-b border-border-subtle'>Reason</th>
                <th className='p-2 border-b border-border-subtle'>By</th>
              </tr>
            </thead>
            <tbody>
              {discards.length === 0 ? (
                <tr>
                  <td colSpan={7} className='p-4 text-center text-text-tertiary'>
                    No discarded issues match the current filters.
                  </td>
                </tr>
              ) : (
                discards.slice(0, ROW_CAP).map((d) => {
                  const groupLabel = labelForAgent(agentGroupKey(d, grouping));
                  const title = variantTitle(d);
                  const category = REASON_BY_ID[d.reason_category] || REASON_BY_ID.other;
                  const link = d.scan_id ? hubScanUrl(hubEnv, d.scan_id) : null;
                  return (
                    <tr key={d.issue_id} className='align-top'>
                      <td className='p-2 border-b border-border-subtle whitespace-nowrap text-text-secondary'>
                        {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}
                      </td>
                      <td className='p-2 border-b border-border-subtle text-text-primary' title={title}>
                        {groupLabel}
                        {grouping === "family" && title !== groupLabel && (
                          <span className='block text-caption text-text-tertiary truncate max-w-[220px]'>{title}</span>
                        )}
                      </td>
                      <td className='p-2 border-b border-border-subtle text-text-primary'>{websiteName(d.website_id)}</td>
                      <td className='p-2 border-b border-border-subtle text-text-secondary'>{d.severity || "—"}</td>
                      <td className='p-2 border-b border-border-subtle text-text-primary'>
                        {link ? (
                          <a href={link} target='_blank' rel='noopener noreferrer' className='text-accent-purple hover:underline' title={`Open scan ${d.scan_id} on the Hub`}>
                            {d.issue_title || d.issue_id} ↗
                          </a>
                        ) : (
                          d.issue_title || "—"
                        )}
                      </td>
                      <td className='p-2 border-b border-border-subtle text-text-primary max-w-md'>
                        <span className='inline-flex items-center gap-1 text-caption text-text-secondary mr-1.5' title={REASON_GROUPS[category.group].label}>
                          <span className='inline-block w-2 h-2 rounded-full' style={{ backgroundColor: category.color }} />
                          {category.label}
                        </span>
                        {d.discard_reason || <span className='text-text-tertiary'>(no reason)</span>}
                      </td>
                      <td className='p-2 border-b border-border-subtle text-text-secondary'>{d.discarded_by || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {discards.length > ROW_CAP && (
            <p className='p-2 text-caption text-text-tertiary'>
              Showing first {ROW_CAP} of {discards.length.toLocaleString()}. Export CSV for the full list.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
